// src/types/user.ts
import { z } from "zod";
var UserSettingsSchema = z.object({
  theme: z.enum(["light", "dark", "system"])
});
var NotificationSettingsSchema = z.object({
  registeredDeviceCount: z.number().int().min(0).max(20)
});
var CreateUserSchema = z.object({
  userId: z.string().min(1)
});
function userToDocument(user) {
  return {
    ...user,
    lastLoginAt: user.lastLoginAt.toISOString(),
    createdAt: user.createdAt.toISOString(),
    updatedAt: user.updatedAt.toISOString()
  };
}
function parseNotificationSettingsFromDoc(raw) {
  if (raw && typeof raw === "object") {
    const o = raw;
    if (typeof o.registeredDeviceCount === "number" && Number.isFinite(o.registeredDeviceCount)) {
      return {
        registeredDeviceCount: Math.max(0, Math.min(20, Math.floor(o.registeredDeviceCount)))
      };
    }
    if (Array.isArray(o.fcmTokens)) {
      return { registeredDeviceCount: o.fcmTokens.length };
    }
  }
  return getDefaultNotificationSettings();
}
function documentToUser(doc) {
  return {
    ...doc,
    notifications: parseNotificationSettingsFromDoc(doc.notifications),
    householdIds: doc.householdIds || [],
    activeHouseholdId: doc.activeHouseholdId || null,
    lastLoginAt: new Date(doc.lastLoginAt),
    createdAt: new Date(doc.createdAt),
    updatedAt: new Date(doc.updatedAt)
  };
}
function getDefaultUserSettings() {
  return { theme: "system" };
}
function getDefaultNotificationSettings() {
  return {
    registeredDeviceCount: 0
  };
}
function userToProfile(user) {
  const n = user.notifications.registeredDeviceCount;
  return {
    userId: user.userId,
    settings: user.settings,
    notifications: {
      enabled: n > 0,
      tokenCount: n
    },
    householdIds: user.householdIds,
    activeHouseholdId: user.activeHouseholdId,
    lastLoginAt: user.lastLoginAt.toISOString()
  };
}

// src/types/household.ts
import { z as z2 } from "zod";
var CreateHouseholdSchema = z2.object({
  name: z2.string().min(1).max(100).trim()
});
var UpdateHouseholdSchema = z2.object({
  householdId: z2.string().min(1),
  name: z2.string().min(1).max(100).trim()
});
var LeaveHouseholdSchema = z2.object({
  householdId: z2.string().min(1)
});
var SetActiveHouseholdSchema = z2.object({
  householdId: z2.string().min(1)
});
function householdToDocument(household) {
  return {
    ...household,
    createdAt: household.createdAt.toISOString(),
    updatedAt: household.updatedAt.toISOString()
  };
}
function documentToHousehold(doc) {
  return {
    ...doc,
    createdAt: new Date(doc.createdAt),
    updatedAt: new Date(doc.updatedAt)
  };
}

// src/types/invitation.ts
import { z as z3 } from "zod";
var CreateInvitationSchema = z3.object({
  householdId: z3.string().min(1),
  maxUses: z3.number().int().min(1).max(50).nullish().transform((v) => v ?? void 0),
  expiryHours: z3.number().int().min(1).max(720).nullish().transform((v) => v ?? void 0)
});
var AcceptInvitationSchema = z3.object({
  token: z3.string().min(1)
});
var RevokeInvitationSchema = z3.object({
  invitationId: z3.string().min(1)
});
var GetInvitationDetailsSchema = z3.object({
  token: z3.string().min(1)
});
function invitationToDocument(inv) {
  return {
    ...inv,
    expiresAt: inv.expiresAt.toISOString(),
    createdAt: inv.createdAt.toISOString()
  };
}
function documentToInvitation(doc) {
  return {
    ...doc,
    expiresAt: new Date(doc.expiresAt),
    createdAt: new Date(doc.createdAt)
  };
}

// src/types/shop.ts
import { z as z4 } from "zod";
var VisitPeriodSchema = z4.object({
  value: z4.number().int().min(1).max(365),
  unit: z4.enum(["days", "weeks", "months"])
});
var CreateShopSchema = z4.object({
  householdId: z4.string().min(1),
  name: z4.string().min(1).max(100).trim(),
  visitPeriod: VisitPeriodSchema.nullable().optional()
});
var UpdateShopSchema = z4.object({
  householdId: z4.string().min(1),
  shopId: z4.string().min(1),
  name: z4.string().min(1).max(100).trim().optional(),
  visitPeriod: VisitPeriodSchema.nullable().optional()
});
var DeleteShopSchema = z4.object({
  householdId: z4.string().min(1),
  shopId: z4.string().min(1)
});
var ReorderShopsSchema = z4.object({
  householdId: z4.string().min(1),
  orderedShopIds: z4.array(z4.string().min(1))
});
var ManageShopSchema = z4.discriminatedUnion("action", [
  z4.object({ action: z4.literal("create"), data: CreateShopSchema }),
  z4.object({ action: z4.literal("update"), data: UpdateShopSchema }),
  z4.object({ action: z4.literal("delete"), data: DeleteShopSchema }),
  z4.object({ action: z4.literal("reorder"), data: ReorderShopsSchema })
]);
function shopToDocument(shop) {
  const doc = {
    shopId: shop.shopId,
    householdId: shop.householdId,
    name: shop.name,
    visitPeriod: shop.visitPeriod,
    lastVisitedAt: shop.lastVisitedAt?.toISOString() ?? null,
    lastNotifiedAt: shop.lastNotifiedAt?.toISOString() ?? null,
    createdAt: shop.createdAt.toISOString(),
    updatedAt: shop.updatedAt.toISOString()
  };
  if (shop.sortOrder != null) {
    doc.sortOrder = shop.sortOrder;
  }
  return doc;
}
function documentToShop(doc) {
  return {
    ...doc,
    sortOrder: typeof doc.sortOrder === "number" ? doc.sortOrder : null,
    lastVisitedAt: doc.lastVisitedAt ? new Date(doc.lastVisitedAt) : null,
    lastNotifiedAt: doc.lastNotifiedAt ? new Date(doc.lastNotifiedAt) : null,
    createdAt: new Date(doc.createdAt),
    updatedAt: new Date(doc.updatedAt)
  };
}
function sortShopDocumentsByDisplayOrder(shops) {
  return [...shops].sort((a, b) => {
    const ao = a.sortOrder;
    const bo = b.sortOrder;
    if (ao != null && bo != null && ao !== bo) return ao - bo;
    if (ao != null && bo == null) return -1;
    if (ao == null && bo != null) return 1;
    return a.createdAt.localeCompare(b.createdAt) || a.name.localeCompare(b.name);
  });
}

// src/types/product.ts
import { z as z5 } from "zod";
var CreateProductSchema = z5.object({
  householdId: z5.string().min(1),
  name: z5.string().min(1).max(200).trim(),
  brand: z5.string().max(200).nullable().optional(),
  barcode: z5.string().max(50).nullable().optional(),
  shopId: z5.string().nullable().optional(),
  defaultQuantity: z5.number().positive().nullable().optional(),
  unit: z5.string().max(20).nullable().optional()
});
var UpdateProductSchema = z5.object({
  householdId: z5.string().min(1),
  productId: z5.string().min(1),
  name: z5.string().min(1).max(200).trim().optional(),
  brand: z5.string().max(200).nullable().optional(),
  barcode: z5.string().max(50).nullable().optional(),
  shopId: z5.string().nullable().optional(),
  defaultQuantity: z5.number().positive().nullable().optional(),
  unit: z5.string().max(20).nullable().optional()
});
var DeleteProductSchema = z5.object({
  householdId: z5.string().min(1),
  productId: z5.string().min(1)
});
var ManageProductSchema = z5.discriminatedUnion("action", [
  z5.object({ action: z5.literal("create"), data: CreateProductSchema }),
  z5.object({ action: z5.literal("update"), data: UpdateProductSchema }),
  z5.object({ action: z5.literal("delete"), data: DeleteProductSchema })
]);
function productToDocument(product) {
  return {
    ...product,
    createdAt: product.createdAt.toISOString(),
    updatedAt: product.updatedAt.toISOString()
  };
}
function documentToProduct(doc) {
  return {
    ...doc,
    createdAt: new Date(doc.createdAt),
    updatedAt: new Date(doc.updatedAt)
  };
}

// src/types/item.ts
import { z as z6 } from "zod";
var AddItemSchema = z6.object({
  householdId: z6.string().min(1),
  name: z6.string().min(1).max(200).trim(),
  brand: z6.string().max(200).nullable().optional(),
  productId: z6.string().optional(),
  barcode: z6.string().max(50).nullable().optional(),
  quantity: z6.number().positive().nullable().optional(),
  unit: z6.string().max(20).nullable().optional(),
  shopId: z6.string().nullable().optional()
});
var CompleteItemSchema = z6.object({
  householdId: z6.string().min(1),
  itemId: z6.string().min(1)
});
var UncompleteItemSchema = z6.object({
  householdId: z6.string().min(1),
  itemId: z6.string().min(1)
});
var UpdateItemSchema = z6.object({
  householdId: z6.string().min(1),
  itemId: z6.string().min(1),
  name: z6.string().min(1).max(200).trim().optional(),
  quantity: z6.number().positive().nullable().optional(),
  unit: z6.string().max(20).nullable().optional(),
  shopId: z6.string().nullable().optional()
});
var RemoveItemSchema = z6.object({
  householdId: z6.string().min(1),
  itemId: z6.string().min(1)
});
var ManageShoppingItemSchema = z6.discriminatedUnion("action", [
  z6.object({ action: z6.literal("add"), data: AddItemSchema }),
  z6.object({ action: z6.literal("update"), data: UpdateItemSchema }),
  z6.object({ action: z6.literal("complete"), data: CompleteItemSchema }),
  z6.object({ action: z6.literal("uncomplete"), data: UncompleteItemSchema }),
  z6.object({ action: z6.literal("remove"), data: RemoveItemSchema })
]);
var CloseShoppingSessionSchema = z6.object({
  householdId: z6.string().min(1),
  shopId: z6.string().nullish().transform((v) => v ?? void 0),
  closeAll: z6.boolean().nullish().transform((v) => v ?? void 0)
});
function shoppingItemToDocument(item) {
  return {
    ...item,
    completedAt: item.completedAt?.toISOString() ?? null,
    createdAt: item.createdAt.toISOString(),
    updatedAt: item.updatedAt.toISOString()
  };
}
function documentToShoppingItem(doc) {
  return {
    ...doc,
    completedAt: doc.completedAt ? new Date(doc.completedAt) : null,
    createdAt: new Date(doc.createdAt),
    updatedAt: new Date(doc.updatedAt)
  };
}
export {
  AcceptInvitationSchema,
  AddItemSchema,
  CloseShoppingSessionSchema,
  CompleteItemSchema,
  CreateHouseholdSchema,
  CreateInvitationSchema,
  CreateProductSchema,
  CreateShopSchema,
  CreateUserSchema,
  DeleteProductSchema,
  DeleteShopSchema,
  GetInvitationDetailsSchema,
  LeaveHouseholdSchema,
  ManageProductSchema,
  ManageShopSchema,
  ManageShoppingItemSchema,
  NotificationSettingsSchema,
  RemoveItemSchema,
  ReorderShopsSchema,
  RevokeInvitationSchema,
  SetActiveHouseholdSchema,
  UncompleteItemSchema,
  UpdateHouseholdSchema,
  UpdateItemSchema,
  UpdateProductSchema,
  UpdateShopSchema,
  UserSettingsSchema,
  documentToHousehold,
  documentToInvitation,
  documentToProduct,
  documentToShop,
  documentToShoppingItem,
  documentToUser,
  getDefaultNotificationSettings,
  getDefaultUserSettings,
  householdToDocument,
  invitationToDocument,
  parseNotificationSettingsFromDoc,
  productToDocument,
  shopToDocument,
  shoppingItemToDocument,
  sortShopDocumentsByDisplayOrder,
  userToDocument,
  userToProfile
};
